export const SECURITY_REASONS = Object.freeze({
  HOST_NOT_ALLOWED: 'host-not-allowed',
  HTTPS_REQUIRED: 'https-required',
  FRAMED_ACCESS_BLOCKED: 'framed-access-blocked',
  EMAIL_NOT_ALLOWED: 'email-not-allowed',
  ADMIN_DIRECTORY_UNAVAILABLE: 'admin-directory-unavailable',
  SUPERADMIN_REQUIRED: 'superadmin-required',
  SESSION_TIMED_OUT: 'session-timed-out'
})

export const DEFAULT_ALLOWED_HOSTS = Object.freeze([
  'localhost',
  '127.0.0.1',
  '::1',
  'tiw.traxby.com',
  'tiw-stage.huntloc.com'
])

const TRUE_VALUES = new Set(['1', 'true', 'yes', 'on'])
const SECURITY_REASON_MESSAGES = Object.freeze({
  [SECURITY_REASONS.HOST_NOT_ALLOWED]:
    'This TIW environment can be opened only from approved addresses.',
  [SECURITY_REASONS.HTTPS_REQUIRED]:
    'Use the HTTPS address of TIW. Plain HTTP access is blocked.',
  [SECURITY_REASONS.FRAMED_ACCESS_BLOCKED]:
    'TIW cannot be opened inside another page or frame.',
  [SECURITY_REASONS.EMAIL_NOT_ALLOWED]:
    'Your account is not approved for TIW administrator access.',
  [SECURITY_REASONS.ADMIN_DIRECTORY_UNAVAILABLE]:
    'TIW administrator access could not be verified right now. Please contact the TIW superadmin.',
  [SECURITY_REASONS.SUPERADMIN_REQUIRED]:
    'This TIW page is available only to a superadmin account.',
  [SECURITY_REASONS.SESSION_TIMED_OUT]:
    'Your TIW session ended because the browser was inactive for too long.'
})

function normalizeCsvList(value) {
  if (!value) return []

  return String(value)
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
}

function normalizeHostEntry(value) {
  if (!value) return ''

  return String(value)
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//i, '')
    .replace(/[/?#].*$/, '')
    .replace(/^\[(.*)\]$/, '$1')
}

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase()
}

function normalizeDomain(value) {
  return normalizeEmail(value).replace(/^@/, '')
}

function parseBoolean(value, defaultValue = false) {
  if (value == null || value === '') return defaultValue
  return TRUE_VALUES.has(String(value).trim().toLowerCase())
}

function parsePositiveInteger(value, defaultValue = 0) {
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : defaultValue
}

export function isLoopbackHostname(hostname) {
  const normalized = normalizeHostEntry(hostname)
  return normalized === 'localhost' || normalized === '127.0.0.1' || normalized === '::1'
}

export function getSecurityPolicyConfig(env = process.env) {
  const configuredHosts = normalizeCsvList(env.VUE_APP_ALLOWED_HOSTS).map(normalizeHostEntry)

  return {
    allowedHosts: configuredHosts.length ? configuredHosts : [...DEFAULT_ALLOWED_HOSTS],
    allowedEmails: normalizeCsvList(env.VUE_APP_ALLOWED_EMAILS).map(normalizeEmail),
    allowedEmailDomains: normalizeCsvList(env.VUE_APP_ALLOWED_EMAIL_DOMAINS).map(normalizeDomain),
    requireHttps: parseBoolean(env.VUE_APP_REQUIRE_HTTPS, true),
    blockEmbeddedApp: parseBoolean(env.VUE_APP_BLOCK_IFRAME, true),
    idleTimeoutMinutes: parsePositiveInteger(env.VUE_APP_IDLE_TIMEOUT_MINUTES, 0)
  }
}

export function hasConfiguredUserAllowlist(config = getSecurityPolicyConfig()) {
  return Boolean(config.allowedEmails.length || config.allowedEmailDomains.length)
}

export function getSecurityMessage(reason) {
  return SECURITY_REASON_MESSAGES[reason] || 'Access to TIW is blocked by the current security policy.'
}

export function createSecurityDecision(allowed, reason = '', details = {}) {
  return {
    allowed,
    reason,
    message: reason ? getSecurityMessage(reason) : '',
    ...details
  }
}

export function createSecurityPolicyError(decision) {
  const error = new Error(decision?.message || getSecurityMessage())
  error.name = 'SecurityPolicyError'
  error.code = 'tiw/security-policy-blocked'
  error.reason = decision?.reason || ''
  return error
}

export function evaluateEnvironmentAccess(
  browserContext = typeof window === 'undefined'
    ? { location: { protocol: 'https:', hostname: '', host: '' }, isEmbedded: false }
    : {
        location: window.location,
        isEmbedded: window.top !== window.self
      },
  config = getSecurityPolicyConfig()
) {
  const location = browserContext?.location || {}
  const hostname = normalizeHostEntry(location.hostname)
  const host = normalizeHostEntry(location.host || location.hostname)
  const protocol = String(location.protocol || '').toLowerCase()
  const isEmbedded = Boolean(browserContext?.isEmbedded)

  if (config.blockEmbeddedApp && isEmbedded) {
    return createSecurityDecision(false, SECURITY_REASONS.FRAMED_ACCESS_BLOCKED)
  }

  if (config.requireHttps && hostname && !isLoopbackHostname(hostname) && protocol !== 'https:') {
    return createSecurityDecision(false, SECURITY_REASONS.HTTPS_REQUIRED, { hostname, host, protocol })
  }

  if (config.allowedHosts.length) {
    const isAllowedHost = config.allowedHosts.some((entry) => entry === host || entry === hostname)

    if (!isAllowedHost) {
      return createSecurityDecision(false, SECURITY_REASONS.HOST_NOT_ALLOWED, { hostname, host, protocol })
    }
  }

  return createSecurityDecision(true, '', { hostname, host, protocol })
}

export function evaluateUserAccess(email, config = getSecurityPolicyConfig()) {
  const normalizedEmail = normalizeEmail(email)

  if (!config.allowedEmails.length && !config.allowedEmailDomains.length) {
    return createSecurityDecision(true)
  }

  if (!normalizedEmail) {
    return createSecurityDecision(false, SECURITY_REASONS.EMAIL_NOT_ALLOWED)
  }

  if (config.allowedEmails.includes(normalizedEmail)) {
    return createSecurityDecision(true)
  }

  const domain = normalizedEmail.split('@')[1] || ''
  if (config.allowedEmailDomains.includes(domain)) {
    return createSecurityDecision(true)
  }

  return createSecurityDecision(false, SECURITY_REASONS.EMAIL_NOT_ALLOWED)
}

export function createIdleSessionManager({
  idleTimeoutMinutes,
  onTimeout,
  target = typeof window === 'undefined' ? null : window
} = {}) {
  const normalizedIdleTimeoutMinutes = Number(idleTimeoutMinutes)
  const timeoutMs = Number.isFinite(normalizedIdleTimeoutMinutes) && normalizedIdleTimeoutMinutes > 0
    ? normalizedIdleTimeoutMinutes * 60 * 1000
    : 0
  const activityEvents = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart']
  let timeoutId = null

  const clearTimer = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  const reset = () => {
    clearTimer()

    if (!timeoutMs || typeof onTimeout !== 'function') {
      return
    }

    timeoutId = setTimeout(() => {
      timeoutId = null
      onTimeout()
    }, timeoutMs)
  }

  return {
    start() {
      if (!target || !timeoutMs || typeof onTimeout !== 'function') {
        return
      }

      activityEvents.forEach((eventName) => target.addEventListener(eventName, reset))
      reset()
    },
    stop() {
      if (!target) {
        clearTimer()
        return
      }

      activityEvents.forEach((eventName) => target.removeEventListener(eventName, reset))
      clearTimer()
    },
    reset
  }
}
